import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  IEntityCriteriaSimpleOperator,
  IEntitySelectionCriteria,
  ITieredEntitySelectionCriterion,
  IEntityCriteriaCompoundOperator,
} from '../../../../common/types/shared/entitySelection';
import { SimpleOperatorBuilder } from './SimpleOperatorBuilder';
import { CompoundOperatorBuilder } from './CompoundOperatorBuilder';
import { CriterionBuilder } from './CriterionBuilder';
import { QueryableFieldsQuery } from '../../../../types/generated-types';
import {
  cloneCompoundOperator,
  cloneCriterion,
  cloneEntitySelectionCriteria,
  cloneSimpleOperator,
  defaultCompoundOperator,
  defaultCriterion,
  defaultIEntitySelectionCriterion,
  defaultSimpleOperator,
} from '../helpers/constructors';
import {
  isValidCompoundOperator,
  isValidCriterion,
  isValidOperator,
} from '../helpers/validators';

interface EntitySelectionDialogProps {
  dialogOpen: boolean;
  editingCriterionId: string | null;
  editingOperatorId: string | null;
  editingCompoundOperatorId: string | null;
  targetModel: string;
  fieldsData: QueryableFieldsQuery['queryableFields'];
  entitySelectionSpec: IEntitySelectionCriteria;
  onSave: (entitySelectionSpec: IEntitySelectionCriteria) => void;
  onCancel: () => void;
  onError: (message: string) => void;
  simplified?: boolean;
}

export const EditEntityDialog: React.FC<EntitySelectionDialogProps> = ({
  dialogOpen,
  editingCriterionId,
  editingOperatorId,
  editingCompoundOperatorId,
  targetModel,
  fieldsData,
  entitySelectionSpec,
  onSave,
  onCancel,
  onError,
  simplified = false,
}) => {
  const [entity, setEntity] = React.useState<IEntitySelectionCriteria>(
    defaultIEntitySelectionCriterion(),
  );
  const [initializing, setInitializing] = React.useState(true);
  // New criterion/operator states
  const [newCriterion, setNewCriterion] =
    useState<ITieredEntitySelectionCriterion>(defaultCriterion());
  const [newOperator, setNewOperator] = useState<IEntityCriteriaSimpleOperator>(
    defaultSimpleOperator(),
  );
  const [newCompoundOperator, setNewCompoundOperator] =
    useState<IEntityCriteriaCompoundOperator>(defaultCompoundOperator());

  // Initialize dialog based on assumption that only one entity ID is non-null
  React.useEffect(() => {
    setEntity(
      entitySelectionSpec
        ? cloneEntitySelectionCriteria(entitySelectionSpec)
        : defaultIEntitySelectionCriterion(),
    );

    if (editingCriterionId) {
      // Handle edit criterion
      const entity = entitySelectionSpec.criteria.find(
        (entity: ITieredEntitySelectionCriterion) =>
          entity.id === editingCriterionId,
      );
      if (entity) {
        setNewCriterion(cloneCriterion(entity));
        setInitializing(false);
      } else {
        setNewCriterion(defaultCriterion());
        setInitializing(true);
        onError(
          `Unable to find selected criterion to edit: ${editingCriterionId}`,
        );
      }
    } else if (editingOperatorId) {
      // Handle edit simple operator
      const entity = entitySelectionSpec.simpleOperators?.find(
        (entity: IEntityCriteriaSimpleOperator) =>
          entity.id === editingOperatorId,
      );
      if (entity) {
        setNewOperator(cloneSimpleOperator(entity));
        setInitializing(false);
      } else {
        setNewOperator(defaultSimpleOperator());
        setInitializing(true);
        onError(
          `Unable to find selected simple operator to edit: ${editingOperatorId}`,
        );
      }
    } else if (editingCompoundOperatorId) {
      // Handle edit compound operator
      const entity = entitySelectionSpec.compoundOperators?.find(
        (entity: IEntityCriteriaCompoundOperator) =>
          entity.id === editingCompoundOperatorId,
      );
      if (entity) {
        setNewCompoundOperator(cloneCompoundOperator(entity));
        setInitializing(false);
      } else {
        setNewCompoundOperator(defaultCompoundOperator());
        setInitializing(true);
        onError(
          `Unable to find selected compound operator to edit: ${editingCompoundOperatorId}`,
        );
      }
    } else {
      // Handle create new criterion entity
      setNewCriterion(defaultCriterion());
      setInitializing(false);
    }
  }, [
    entitySelectionSpec,
    editingCriterionId,
    editingOperatorId,
    editingCompoundOperatorId,
    onError,
  ]);

  // Update handlers for various entity types as specific edit dialogs update values
  const onCriterionChange = (
    updatedCriterion: ITieredEntitySelectionCriterion,
  ) => {
    setNewCriterion(updatedCriterion);
  };

  const onOperatorChange = (updatedOperator: IEntityCriteriaSimpleOperator) => {
    setNewOperator(updatedOperator);
  };

  const onCompoundOperatorChange = (
    updatedCompoundOperator: IEntityCriteriaCompoundOperator,
  ) => {
    setNewCompoundOperator(updatedCompoundOperator);
  };

  // Save new or updated entity. Only one should be applicable based on non-null id
  const handleSave = () => {
    let newEntity: IEntitySelectionCriteria;
    if (editingCriterionId) {
      // Update existing criterion
      newEntity = {
        ...entity,
        criteria: entity.criteria.map((c: ITieredEntitySelectionCriterion) =>
          c.id === editingCriterionId ? newCriterion : c,
        ),
      };
    } else if (editingOperatorId) {
      newEntity = {
        ...entity,
        simpleOperators: (entity.simpleOperators ?? []).map(
          (o: IEntityCriteriaSimpleOperator) =>
            o.id === editingOperatorId ? newOperator : o,
        ),
      };
    } else if (editingCompoundOperatorId) {
      newEntity = {
        ...entity,
        compoundOperators: (entity.compoundOperators ?? []).map(
          (o: IEntityCriteriaCompoundOperator) =>
            o.id === editingCompoundOperatorId ? newCompoundOperator : o,
        ),
      };
    } else {
      newEntity = {
        ...entity,
        criteria: [...entity.criteria, newCriterion],
      };
    }
    // Reset new criterion and close dialog
    setNewCriterion(defaultCriterion());
    setNewOperator(defaultSimpleOperator());
    setNewCompoundOperator(defaultCompoundOperator());
    setInitializing(true);
    onSave(newEntity);
    onCancel();
  };

  return initializing ? (
    <div>Loading ...</div>
  ) : (
    <Dialog open={dialogOpen} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        {((): string => {
          if (simplified) return `Specify ${targetModel} Curtailment Edits`;
          if (editingCriterionId) return `Edit Criterion (${targetModel})`;
          if (editingOperatorId) return `Edit Simple Operator ${targetModel}`;
          if (editingCompoundOperatorId)
            return `Edit Compound Operator (${targetModel})`;
          return `Add Selection Criterion (${targetModel})`;
        })()}
      </DialogTitle>
      <DialogContent
        sx={{
          height: 'calc(100vh - 200px)',
          minHeight: '400px',
          overflow: 'auto',
          p: 2,
        }}
      >
        <Box sx={{ mt: 2 }}>
          {editingCompoundOperatorId ? (
            <CompoundOperatorBuilder
              operator={newCompoundOperator}
              onChange={onCompoundOperatorChange}
              onRemove={() => {}}
              targetModel={targetModel}
              fieldsData={fieldsData}
            />
          ) : editingOperatorId ? (
            <SimpleOperatorBuilder
              selectionSpec={newOperator}
              onChange={(value) => {
                if (typeof value === 'string') {
                  onOperatorChange({
                    ...newOperator,
                    operator: value,
                  });
                } else {
                  onOperatorChange(value);
                }
              }}
              fieldsData={fieldsData}
            />
          ) : (
            <CriterionBuilder
              targetModel={targetModel}
              fieldsData={fieldsData}
              selectionSpec={newCriterion}
              onChange={onCriterionChange}
              tier={0}
              simplified={simplified}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        {editingCompoundOperatorId ? (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isValidCompoundOperator(newCompoundOperator)}
          >
            Save Compound Operator
          </Button>
        ) : editingOperatorId ? (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isValidOperator(newOperator)}
          >
            Save Simple Operator
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!isValidCriterion(newCriterion)}
          >
            {editingCriterionId ? 'Update Criterion' : 'Save Criterion'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
